import React, { useEffect, useId, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaChevronRight, FaUser } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { resolveMediaUrl } from '../../../hooks/useStreamBootstrap.js';
import { useStreamSearch } from '../../../hooks/useStreamSearch.js';
import { isSignedIn } from '../../../_helpers/safeRedirect.js';
import SongRequestSheet from './SongRequestSheet.jsx';
import '../../../styles/SongCratePanel.css';
import '../../../styles/SearchResultsPanel.css';
import '../../../styles/StreamCta.css';

/**
 * "Results for '<query>'" (mockup 18, ENG-701) in place of the stream page's
 * right column while the header search holds a query: PERFORMERS (live ones
 * with their watchers, others with their next show) and IN THIS SHOW'S CRATE
 * with a Request each (`GET /public/bands/{id}/stream/search`, ENG-702).
 * Request asks to confirm here until the song page (mockup 22) exists.
 * Esc and Back to chat clear the search, which gives the chat back.
 */
const SearchResultsPanel = ({ bandId, term, onClear, onDone, onSignIn, onOpenSong }) => {
  const { t, format } = useLocale();
  const navigate = useNavigate();
  const headingId = useId();
  const search = useStreamSearch(bandId, term);
  const [picked, setPicked] = useState(null);
  const shown = search.query || term.trim();
  const { performers, songs } = search.results;

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClear();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClear]);

  const request = (song) => {
    // The song page takes the request (mockup 22, ENG-709).
    if (onOpenSong) {
      onOpenSong(song);
      return;
    }
    if (!isSignedIn()) {
      onSignIn?.();
      return;
    }
    setPicked(song);
  };

  const settled = !search.loading && search.query !== '';

  return (
    <section className="crate-panel search-panel" aria-labelledby={headingId} data-testid="search-panel">
      <header className="crate-panel-header search-panel-header">
        <h2 id={headingId} className="search-panel-title">{t('stream.search.resultsFor', { query: shown })}</h2>
        <button type="button" className="search-panel-back" onClick={onClear} data-testid="search-back-to-chat">
          {t('stream.search.backToChat')}
        </button>
      </header>

      <div className="crate-panel-scroll">
        {search.error && <p className="crate-panel-status" role="alert">{t('stream.search.failed')}</p>}

        <h3 className="search-panel-section">{t('stream.search.performers')}</h3>
        {settled && performers.items.length === 0 && (
          <p className="search-panel-empty" data-testid="search-no-performers">{t('stream.search.noPerformers')}</p>
        )}
        <ul className="search-panel-list">
          {performers.items.map((p) => {
            const avatar = resolveMediaUrl(p.avatarUrl);
            return (
              <li key={p.id}>
                <button
                  type="button"
                  className="search-row"
                  onClick={() => (p.isCurrent ? onClear() : navigate(p.isLive ? `/performers/${p.id}/stream` : `/performers/${p.id}`))}
                  data-testid={`search-performer-${p.id}`}
                >
                  <span className="search-row-avatar">{avatar ? <img src={avatar} alt="" /> : <FaUser aria-hidden="true" />}</span>
                  <span className="search-row-text">
                    <span className="search-row-title">{p.name}</span>
                    <span className="search-row-meta">
                      {p.isLive ? (
                        <>
                          <span className="search-row-live">{t('stream.search.live')}</span>
                          {t('stream.performer.watching', { count: p.viewerCount, formattedCount: format.number(p.viewerCount) })}
                        </>
                      ) : p.nextShow ? (
                        t('stream.search.nextShow', { when: format.dateTime(p.nextShow, 'eventListing') })
                      ) : (
                        t('stream.search.noShow')
                      )}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <h3 className="search-panel-section">{t('stream.search.crate')}</h3>
        {settled && songs.items.length === 0 && (
          <p className="search-panel-empty" data-testid="search-no-songs">{t('stream.search.noSongs')}</p>
        )}
        <ul className="search-panel-list">
          {songs.items.map((song) => {
            const art = resolveMediaUrl(song.artworkUrl);
            return (
              <li key={song.id} className="search-row" data-testid={`search-song-${song.id}`}>
                <span className="search-row-art">{art ? <img src={art} alt="" /> : <span className="search-row-vinyl" />}</span>
                <span className="search-row-text">
                  <span className="search-row-title">{song.title}</span>
                  <span className="search-row-meta">{song.artist}</span>
                </span>
                <button type="button" className="search-row-request stream-cta" onClick={() => request(song)}>
                  {t('stream.search.request')}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <footer className="search-panel-footer">
        <Link className="search-panel-discovery" to={`/performers?name=${encodeURIComponent(shown)}`}>
          {t('stream.search.seeAll')}
          <FaChevronRight className="search-panel-discovery-chevron rtl-mirror" aria-hidden="true" />
        </Link>
      </footer>

      {picked && (
        <SongRequestSheet key={picked.id} bandId={bandId} song={picked} onCancel={() => setPicked(null)} onDone={onDone} />
      )}
    </section>
  );
};

export default SearchResultsPanel;
